// API Security — Overview / dashboard. Matches the design provided in
// Screenshot 2026-05-10 at 11.03.39: header + summary line, 6 metric tiles
// with sparklines, two-column split below (endpoints table + selected
// endpoint detail panel with risk factors, traffic 24h, schema drift).
import React from "react";
import { useNavigate } from "react-router-dom";
import {
  Stack, Box, Typography, Grid, Chip, Button, Tooltip,
  Table, TableBody, TableCell, TableHead, TableRow, TableContainer,
  CircularProgress, TablePagination, Select, MenuItem,
  Autocomplete, IconButton,
} from "@mui/material";
import EditIcon from "@mui/icons-material/EditOutlined";
import CheckIcon from "@mui/icons-material/Check";
import CloseIcon from "@mui/icons-material/Close";
import {
  ChevronLeft, Upload, LayoutList, AlertTriangle, ShieldOff, Eye, Gauge, Activity,
} from "lucide-react";
import asec from "../../../../../utils/axios/v1/asecAxios";
import SectionHelp from "./components/SectionHelp";
import colors from "../../../../../utils/colors";
import { useNotification } from "../../../../../contexts/NotificationContext";
import AsecPage, { AsecPanel, SectionTitle, SubLabel } from "./AsecPage";
import Sparkline from "./Sparkline";
import LayerdIcon from "../../../../common/LayerdIcon";
import { GhostButton, PrimaryButton } from "../../../../common/Button";
import { wafOverviewSubSx } from "../web_application/components/WAFOverviewMetricCard";
import {
  asecBtnOutlinedSm,
  asecBtnPrimarySm,
  asecBtnTextSm,
  filterSelectSx,
  AsecTextField,
} from "./asecUi";
import { AsecMetricStrip } from "./components/asecPrimitives";

const METHOD_COLORS = {
  GET:    { bg: "rgba(16, 185, 129, 0.10)", fg: "#10b981" },
  POST:   { bg: "rgba(99, 113, 247, 0.10)", fg: "#6371F7" },
  PUT:    { bg: "rgba(245, 158, 11, 0.10)", fg: "#b45309" },
  PATCH:  { bg: "rgba(245, 158, 11, 0.10)", fg: "#b45309" },
  DELETE: { bg: "rgba(239, 68, 68, 0.10)", fg: "#ef4444" },
};

function MethodTag({ method }) {
  const sx = METHOD_COLORS[(method || "").toUpperCase()] || { bg: colors.neutral[200], fg: colors.neutral[800] };
  return (
    <Box sx={{
      display: "inline-flex", alignItems: "center", justifyContent: "center",
      px: 1, height: 22, minWidth: 56, borderRadius: "4px",
      backgroundColor: sx.bg, color: sx.fg,
fontSize: 12, fontWeight: 700, letterSpacing: 0.3,
    }}>{(method || "").toUpperCase()}</Box>
  );
}

function RiskTag({ level }) {
  const c = {
    crit: { bg: "rgba(239,68,68,0.14)", fg: "#b91c1c" },
    high: { bg: "rgba(245,158,11,0.14)", fg: "#b45309" },
    med:  { bg: "rgba(99,113,247,0.10)", fg: "#4338ca" },
    low:  { bg: "rgba(16,185,129,0.10)", fg: "#0f766e" },
  }[level] || { bg: colors.neutral[200], fg: colors.neutral[800] };
  return (
    <Box sx={{
      display: "inline-flex", alignItems: "center", justifyContent: "center",
      px: 1.5, height: 22, borderRadius: "4px",
      backgroundColor: c.bg, color: c.fg,
fontSize: 12, fontWeight: 600,
    }}>{level}</Box>
  );
}

// Heuristic risk score for an endpoint until backend exposes it. Returns
// {level, factors}.
function deriveRisk(ep) {
  const factors = [];
  let score = 0;
  const sensitivity = ep.sensitivity_tags || [];
  if (sensitivity.includes("financial") || sensitivity.includes("government_ids") || sensitivity.includes("digital_secrets")) {
    factors.push("Handles sensitive payloads");
    score += 30;
  }
  if (sensitivity.includes("personal_identity")) {
    factors.push("Carries PII");
    score += 20;
  }
  if (ep.classification === "shadow") {
    factors.push("Not in published spec");
    score += 25;
  }
  if (ep.classification === "deprecated") {
    factors.push("Deprecated but still receiving traffic");
    score += 15;
  }
  if (!ep.spec_id) {
    factors.push("No schema reference");
    score += 10;
  }
  if (["POST", "PUT", "PATCH", "DELETE"].includes(ep.method)) {
    factors.push("State-changing method");
    score += 5;
  }
  let level = "low";
  if (score >= 60) level = "crit";
  else if (score >= 35) level = "high";
  else if (score >= 15) level = "med";
  return { level, factors, score };
}

// Sparkline tiles require time-series counts that the control-plane
// doesn't expose yet (would need an /endpoint_counts?window=14d
// rollup, or a CH-aggregation endpoint). Until then, render the
// sparkline area empty rather than synthetic data — a security
// dashboard showing fake trends is worse than showing none.
const EMPTY_SPARKLINE = [];

function metricSubWithSparkline(text, sparkline, sparkTone) {
  return (
    <Stack direction="row" alignItems="center" justifyContent="space-between" spacing={1}>
      <Typography sx={wafOverviewSubSx}>{text}</Typography>
      <Sparkline data={sparkline} tone={sparkTone} width={84} height={24} />
    </Stack>
  );
}

function buildOverviewMetrics(totals, timeseries, formatCalls) {
  return [
    {
      key: "discovered",
      icon: LayoutList,
      highlight: true,
      label: "Discovered",
      value: totals.totalCount ?? "—",
      sub: metricSubWithSparkline("last 14d", timeseries?.discovered || EMPTY_SPARKLINE, "info"),
    },
    {
      key: "risky",
      icon: AlertTriangle,
      label: "Risky",
      value: totals.risky ?? "—",
      valueSx: totals.risky > 0 ? { color: "#A11228" } : undefined,
      sub: totals.riskCrit != null
        ? `${totals.riskHigh} high · ${totals.riskCrit} crit`
        : undefined,
    },
    {
      key: "unauth",
      icon: ShieldOff,
      label: "Unauth",
      value: totals.unauth ?? "—",
      sub: "needs review",
    },
    {
      key: "shadow",
      icon: Eye,
      label: "Shadow",
      value: totals.shadow ?? "—",
      sub: "not in spec",
    },
    {
      key: "p95",
      icon: Gauge,
      label: "Avg P95",
      value: totals.p95Ms ?? "—",
      unit: totals.p95Ms != null ? "ms" : undefined,
      sub: metricSubWithSparkline("last 30d", timeseries?.p95_ms || EMPTY_SPARKLINE, "info"),
    },
    {
      key: "calls",
      icon: Activity,
      label: "API calls",
      value: formatCalls(totals.calls30d),
      sub: metricSubWithSparkline("last 30d", timeseries?.calls || EMPTY_SPARKLINE, "info"),
    },
  ];
}

function RiskFactorList({ factors }) {
  return (
    <Box component="ul" sx={{ pl: 3, m: 0 }}>
      {factors.map((f, i) => (
        <Typography key={i} component="li" sx={ { fontSize: 14, color: colors.neutral[800], lineHeight: "22px" }}>
          {f}
        </Typography>
      ))}
    </Box>
  );
}

function EndpointDetailPanel({ ep, drift, navigate, siteId, tagOptions, onSavedTags }) {
  // Per-endpoint 24h traffic series. Refetched whenever the selected
  // endpoint changes. Stale data from the previously-selected
  // endpoint is cleared immediately so the sparkline doesn't lie.
  const [traffic24h, setTraffic24h] = React.useState(null);
  React.useEffect(() => {
    if (!ep || !siteId) { setTraffic24h(null); return; }
    let cancelled = false;
    asec.endpointTraffic24h(siteId, ep.id).then((r) => {
      if (cancelled) return;
      const calls = Array.isArray(r.data?.calls) ? r.data.calls : [];
      setTraffic24h(calls);
    }).catch(() => { if (!cancelled) setTraffic24h([]); });
    return () => { cancelled = true; };
  }, [ep && ep.id, siteId]);

  // Inline label editor. Mirrors the Catalog page's per-row tag
  // editor — Autocomplete with the site's known tag set as
  // suggestions, freeSolo so operators can add new tags, Save fires
  // PATCH /endpoints/:id with the new array.
  const [editingTags, setEditingTags] = React.useState(false);
  const [tagsDraft, setTagsDraft] = React.useState([]);
  const [tagsSaving, setTagsSaving] = React.useState(false);
  React.useEffect(() => {
    // When the selected endpoint changes, exit edit mode and reset
    // the draft. Keeps the editor state per-endpoint coherent.
    setEditingTags(false);
    setTagsDraft(Array.isArray(ep?.sensitivity_tags) ? ep.sensitivity_tags.slice() : []);
  }, [ep && ep.id]);
  const saveTags = async () => {
    if (!ep) return;
    setTagsSaving(true);
    try {
      await asec.endpointPatch(siteId, ep.id, { sensitivity_tags: tagsDraft });
      setEditingTags(false);
      if (onSavedTags) onSavedTags(ep.id, tagsDraft);
    } finally {
      setTagsSaving(false);
    }
  };
  const cancelTags = () => {
    setEditingTags(false);
    setTagsDraft(Array.isArray(ep?.sensitivity_tags) ? ep.sensitivity_tags.slice() : []);
  };

  if (!ep) {
    return (
      <AsecPanel sx={{ maxWidth: "100%", height: "100%" }}>
        <SectionTitle mb={2}>Selected</SectionTitle>
        <SubLabel>Click any endpoint to see risk factors, traffic and schema drift.</SubLabel>
      </AsecPanel>
    );
  }
  const risk = deriveRisk(ep);
  const driftForEp = (drift || []).filter((d) =>
    d.host === ep.host && d.path_template === ep.path_template
  );
  const epTags = Array.isArray(ep.sensitivity_tags) ? ep.sensitivity_tags : [];
  return (
    <AsecPanel sx={{ maxWidth: "100%" }}>
      <Typography sx={ { fontSize: 12, letterSpacing: 0.6, color: colors.neutral[600], fontWeight: 600, mb: 1 }}>
        SELECTED
      </Typography>
      <Stack direction="row" alignItems="center" spacing={2} mb={1}>
        <MethodTag method={ep.method} />
        <Typography sx={{ fontSize: 16, color: colors.neutral[1000] }}>{ep.path_template}</Typography>
      </Stack>
      <Box mb={3}><RiskTag level={risk.level} /></Box>

      <Typography sx={ { fontSize: 12, letterSpacing: 0.6, color: colors.neutral[600], fontWeight: 600, mb: 1 }}>RISK FACTORS</Typography> <SectionHelp tooltip="What's contributing to this endpoint's risk score. Higher-weighted factors first; click through to the source panel." size="small" />
      {risk.factors.length === 0
        ? <SubLabel>No risk factors detected.</SubLabel>
        : <RiskFactorList factors={risk.factors} />
      }

      {/* LABELS — inline editor mirrors the Catalog page's per-row
          tag editor. View mode shows chips; Edit mode swaps in an
          Autocomplete (freeSolo so operators can add new tags).
          Saves via PATCH /endpoints/:id; on success bubbles back to
          the parent so the table row re-renders without a refetch. */}
      <Box mt={3}>
        <Stack direction="row" alignItems="center" justifyContent="space-between" mb={1}>
          <Typography sx={ { fontSize: 12, letterSpacing: 0.6, color: colors.neutral[600], fontWeight: 600 }}>
            LABELS
          </Typography>
          {!editingTags ? (
            <Tooltip title="Edit labels">
              <IconButton size="small" onClick={() => setEditingTags(true)} sx={{ color: colors.neutral[600], p: 0.25 }}>
                <EditIcon fontSize="inherit" />
              </IconButton>
            </Tooltip>
          ) : (
            <Stack direction="row" spacing={0.5}>
              <Tooltip title="Save">
                <span>
                  <IconButton size="small" onClick={saveTags} disabled={tagsSaving}
                    sx={{ color: "#0f766e", p: 0.25 }}>
                    <CheckIcon fontSize="inherit" />
                  </IconButton>
                </span>
              </Tooltip>
              <Tooltip title="Cancel">
                <IconButton size="small" onClick={cancelTags} sx={{ color: colors.neutral[600], p: 0.25 }}>
                  <CloseIcon fontSize="inherit" />
                </IconButton>
              </Tooltip>
            </Stack>
          )}
        </Stack>
        {editingTags ? (
          <Autocomplete
            multiple
            freeSolo
            size="small"
            options={Array.isArray(tagOptions) ? tagOptions : []}
            value={tagsDraft}
            onChange={(_, v) => setTagsDraft(v.map((s) => String(s).trim()).filter(Boolean))}
            renderInput={(params) => (
              <AsecTextField {...params} placeholder="Add a label (e.g. auth, admin, financial)" />
            )}
            sx={{ "& .MuiOutlinedInput-root": { borderRadius: "6px" } }}
          />
        ) : epTags.length === 0 ? (
          <SubLabel>No labels yet — click the pencil to add one.</SubLabel>
        ) : (
          <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap>
            {epTags.map((t) => (
              <Chip key={t} label={t} size="small"
                sx={{
fontSize: 12, height: 22,
                  backgroundColor: "rgba(245, 158, 11, 0.10)", color: "#b45309",
                }} />
            ))}
          </Stack>
        )}
      </Box>

      <Box mt={3} mb={1}>
        <Typography sx={ { fontSize: 12, letterSpacing: 0.6, color: colors.neutral[600], fontWeight: 600, mb: 1 }}>TRAFFIC · 24H</Typography> <SectionHelp tooltip="Per-hour request volume over the last day. Pulled from ClickHouse, zero-filled for missing buckets." size="small" />
        <Sparkline data={traffic24h || []} tone="info" width={400} height={48} strokeWidth={2} />
        <Stack direction="row" justifyContent="space-between" mt={1}>
          <Stack>
            <Typography sx={{ fontSize: 12, color: colors.neutral[600] }}>Calls · 24h</Typography>
            <Typography sx={{ fontSize: 14, fontWeight: 600 }}>
              {traffic24h === null ? "…" : traffic24h.reduce((a, b) => a + b, 0).toLocaleString()}
            </Typography>
          </Stack>
          <Stack>
            <Typography sx={{ fontSize: 12, color: colors.neutral[600] }}>Calls · 30d</Typography>
            <Typography sx={{ fontSize: 14, fontWeight: 600 }}>{Number(ep.observation_count_30d || 0).toLocaleString()}</Typography>
          </Stack>
        </Stack>
      </Box>

      <Stack direction="row" spacing={1} mt={3} flexWrap="wrap" useFlexGap>
        <Button size="small" variant="contained" sx={asecBtnPrimarySm}
          onClick={() => navigate("/v2/services/rate_limit_rules")}>Add rate limit</Button>
        <Button size="small" variant="outlined" sx={asecBtnOutlinedSm}
          onClick={() => navigate("/v2/api-security/schema")}>Schema lock</Button>
        <Button size="small" variant="text" sx={asecBtnTextSm}
          onClick={() => navigate("/v2/api-security/schema")}>View spec</Button>
      </Stack>

      {driftForEp.length > 0 && (
        <Box mt={4}>
          <Stack direction="row" justifyContent="space-between" mb={1}>
            <Typography sx={ { fontSize: 14, fontWeight: 600, color: colors.neutral[1000] }}>Schema drift</Typography> <SectionHelp tooltip="Spec coverage signals: shadow endpoints (observed but undocumented) + unused operations (in spec but no traffic) + mismatch (declared but observed extra query keys)." size="small" />
            <SubLabel>vs published</SubLabel>
          </Stack>
          <Stack spacing={1}>
            {driftForEp.slice(0, 6).map((d, i) => (
              <Stack direction="row" key={i} alignItems="center" spacing={1.5}>
                <Chip
                  label={d.kind === "added" ? "+ field" : d.kind === "removed" ? "− field" : d.kind}
                  size="small"
                  sx={{
                    fontSize: 12,
                    backgroundColor: d.kind === "added" ? "rgba(16,185,129,0.10)" : d.kind === "removed" ? "rgba(239,68,68,0.10)" : "rgba(99,113,247,0.10)",
                    color: d.kind === "added" ? "#0f766e" : d.kind === "removed" ? "#b91c1c" : "#4338ca",
                    height: 18,
                  }}
                />
                <Typography sx={{ fontSize: 12, color: colors.neutral[1000] }}>{d.detail?.field || d.detail?.summary || "—"}</Typography>
              </Stack>
            ))}
          </Stack>
        </Box>
      )}
    </AsecPanel>
  );
}

function Body({ siteId }) {
  const navigate = useNavigate();
  const { showError } = useNotification();
  const [endpoints, setEndpoints] = React.useState(null);
  const [endpointsTotal, setEndpointsTotal] = React.useState(0);
  const [page, setPage] = React.useState(0);
  const [pageSize, setPageSize] = React.useState(25);
  const [drift, setDrift] = React.useState(null);
  const [counts, setCounts] = React.useState(null);
  // Per-site rollups for the metric tiles. `aggregates` is PG-driven
  // (status / classification / review_state / risk breakdowns);
  // `traffic` is CH-driven (p95 latency + 30d call count). Both are
  // fetched once per site change and on Refresh — never per page.
  const [aggregates, setAggregates] = React.useState(null);
  const [traffic, setTraffic] = React.useState(null);
  const [filter, setFilter] = React.useState("all");
  const [selected, setSelected] = React.useState(null);

  // Reset to page 0 whenever the filter toggle changes — keeps the
  // displayed page in range as the dataset size shifts under a new
  // filter.
  React.useEffect(() => { setPage(0); }, [filter]);

  // Page+filter live in the endpoints fetch via query params. The
  // per-status totals come from /endpoints_aggregates (one grouped
  // query covering status/classification/review/risk), so changing
  // page doesn't refetch the tile data.
  const reload = React.useCallback(async () => {
    if (!siteId) return;
    try {
      const params = { page, limit: pageSize };
      if (filter === "unauth") params.review_state = "needs_review";
      if (filter === "shadow") params.status = "shadow";
      const [e, d] = await Promise.all([
        asec.endpoints(siteId, params).catch(() => ({ data: { items: [], total: 0 } })),
        asec.driftList(siteId, { limit: 200 }).catch(() => ({ data: [] })),
      ]);
      const items = Array.isArray(e.data?.items) ? e.data.items : [];
      setEndpoints(items);
      setEndpointsTotal(typeof e.data?.total === "number" ? e.data.total : items.length);
      setDrift(Array.isArray(d.data) ? d.data : []);
      if (items.length > 0 && !selected) setSelected(items[0]);
    } catch (e) {
      showError(e?.message || String(e));
      setEndpoints([]); setDrift([]);
    }
  }, [siteId, page, pageSize, filter, selected, showError]);
  React.useEffect(() => { reload(); /* eslint-disable-next-line */ }, [siteId, page, pageSize, filter]);

  // Per-site rollups: aggregates + counts + traffic + timeseries +
  // sensitivity tag catalog. Refetched on site change (or Refresh
  // via the AsecPage refreshKey remount), NOT on every page flip.
  // Five parallel requests; each falls back to null on failure so
  // individual tiles render "-" / empty sparkline without affecting
  // the rest of the dashboard.
  const [timeseries, setTimeseries] = React.useState(null);
  const [tagOptions, setTagOptions] = React.useState([]);
  React.useEffect(() => {
    if (!siteId) return;
    let cancelled = false;
    (async () => {
      const [a, c, t, ts, tags] = await Promise.all([
        asec.endpointAggregates(siteId).catch(() => ({ data: null })),
        asec.endpointCounts(siteId).catch(() => ({ data: {} })),
        asec.trafficSummary(siteId).catch(() => ({ data: null })),
        asec.dashboardTimeseries(siteId).catch(() => ({ data: null })),
        asec.sensitivityTags(siteId).catch(() => ({ data: [] })),
      ]);
      if (cancelled) return;
      setAggregates(a.data || null);
      setCounts(c.data || {});
      setTraffic(t.data || null);
      setTimeseries(ts.data || null);
      // The /sensitivity_tags response is [{tag, count}, ...]; the
      // detail panel's Autocomplete just needs the tag strings.
      setTagOptions(Array.isArray(tags.data) ? tags.data.map((r) => r.tag).filter(Boolean) : []);
    })();
    return () => { cancelled = true; };
  }, [siteId]);

  // Tile metrics. Pulls from three rollups:
  //   counts      → review_state + total + hostname distinct
  //   aggregates  → status + classification + review_state + risk
  //   traffic     → CH-driven p95 + 30d call count
  // Anything missing renders as "-" so the dashboard never asserts a
  // number it can't back up.
  const totals = React.useMemo(() => {
    const c = counts || {};
    const a = aggregates || {};
    const t = traffic || {};
    return {
      totalCount: typeof c.total === "number" ? c.total : null,
      unauth:     typeof c.needs_review === "number" ? c.needs_review : null,
      hostnames:  typeof c.hostnames === "number" ? c.hostnames : null,
      monitored:  typeof a.monitored === "number" ? a.monitored : null,
      shadow:     typeof a.shadow === "number" ? a.shadow : null,
      deprecated: typeof a.deprecated === "number" ? a.deprecated : null,
      risky:      typeof a.risky === "number" ? a.risky : null,
      riskCrit:   a.by_risk ? (a.by_risk.crit || 0) : null,
      riskHigh:   a.by_risk ? (a.by_risk.high || 0) : null,
      p95Ms:      typeof t.p95_ms === "number" ? t.p95_ms : null,
      calls30d:   typeof t.calls_30d === "number" ? t.calls_30d : null,
    };
  }, [counts, aggregates, traffic]);

  // Format the 30d call counter compactly — 20813 → "20.8k",
  // 4.1M → "4.1M", below 1k → as-is.
  const formatCalls = (n) => {
    if (n == null) return "—";
    if (n >= 1_000_000) return `${(n / 1_000_000).toFixed(1)}M`;
    if (n >= 1_000)     return `${(n / 1_000).toFixed(1)}k`;
    return String(n);
  };

  // Filtering happens server-side for "unauth" and "shadow" (via the
  // params in `reload`). "risky" stays a client-side overlay over the
  // current page until backend exposes a risk-score filter — flagged
  // explicitly because filtering by risky on the visible page only
  // skips rows that match the criterion on other pages.
  const filtered = React.useMemo(() => {
    if (!endpoints) return [];
    if (filter === "risky") {
      return endpoints.filter((e) => {
        const r = deriveRisk(e); return r.level === "high" || r.level === "crit";
      });
    }
    return endpoints;
  }, [endpoints, filter]);

  return (
    <Stack spacing={4}>
      {/* Summary line + actions row. Aggregates land via the
          /endpoints_aggregates endpoint so every count here reflects
          the full dataset, not whatever page is currently loaded. */}
      <Stack direction="row" alignItems="center">
        <Typography sx={ { fontSize: 14, color: colors.neutral[700] }}>
          <strong>{totals.totalCount ?? "—"}</strong> endpoints
          {totals.monitored != null ? <> · {totals.monitored} monitored</> : null}
          {totals.unauth != null ? <> · {totals.unauth} needs review</> : null}
          {totals.shadow != null && totals.shadow > 0 ? <> · {totals.shadow} shadow</> : null}
          {totals.deprecated != null && totals.deprecated > 0 ? <> · {totals.deprecated} deprecated</> : null}
          {totals.hostnames != null ? <> · {totals.hostnames} hostnames</> : null}
        </Typography>
      </Stack>

      <AsecMetricStrip metrics={buildOverviewMetrics(totals, timeseries, formatCalls)} />

      {/* Two-column split: endpoints table + selected endpoint detail */}
      <Grid container spacing={3}>
        <Grid item xs={12} md={7}>
          <AsecPanel sx={{ maxWidth: "100%" }}>
            <Stack direction="row" alignItems="center" justifyContent="space-between" mb={3}>
              <SectionTitle mb={0}>Endpoints</SectionTitle>
              <Select
                value={filter}
                onChange={(e) => setFilter(e.target.value)}
                sx={{ ...filterSelectSx, minWidth: 160 }}
              >
                <MenuItem value="all">All ({totals.totalCount != null ? totals.totalCount.toLocaleString() : "—"})</MenuItem>
                <MenuItem value="risky">Risky ({totals.risky != null ? totals.risky.toLocaleString() : "—"})</MenuItem>
                <MenuItem value="unauth">Unauth ({totals.unauth != null ? totals.unauth.toLocaleString() : "—"})</MenuItem>
                <MenuItem value="shadow">Shadow ({totals.shadow != null ? totals.shadow.toLocaleString() : "—"})</MenuItem>
              </Select>
            </Stack>
            {endpoints === null ? <CircularProgress /> :
             filtered.length === 0 ? <SubLabel>No endpoints to show.</SubLabel> : (
              <>
              <TableContainer sx={{ maxHeight: 540 }}>
                <Table size="small" stickyHeader>
                  <TableHead>
                    <TableRow>
                      {["METHOD","PATH","AUTH","RPS","P95","4XX","RISK"].map((h) => (
                        <TableCell key={h} sx={ { fontSize: 12, letterSpacing: 0.6, color: colors.neutral[600], fontWeight: 600, backgroundColor: "white", borderBottom: `1px solid ${colors.neutral[200]}` }}>{h}</TableCell>
                      ))}
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {filtered.map((ep) => {
                      const r = deriveRisk(ep);
                      const isSelected = selected?.id === ep.id;
                      return (
                        <TableRow key={ep.id} hover selected={isSelected} sx={{ cursor: "pointer" }} onClick={() => setSelected(ep)}>
                          <TableCell><MethodTag method={ep.method} /></TableCell>
                          <TableCell sx={{ fontSize: 14, color: colors.neutral[1000] }}>{ep.path_template}</TableCell>
                          <TableCell sx={{ fontSize: 14, color: ep.review_state === "saved" ? "#0f766e" : colors.neutral[700] }}>
                            {ep.review_state === "saved" ? "✓" : ep.review_state === "needs_review" ? <Tooltip title="Needs review"><span>⚠</span></Tooltip> : "—"}
                          </TableCell>
                          <TableCell sx={{ fontSize: 12, color: colors.neutral[700] }}>—</TableCell>
                          <TableCell sx={{ fontSize: 12, color: colors.neutral[700] }}>—</TableCell>
                          <TableCell sx={{ fontSize: 12, color: colors.neutral[700] }}>—</TableCell>
                          <TableCell><RiskTag level={r.level} /></TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </TableContainer>
              <TablePagination
                component="div"
                count={endpointsTotal}
                page={page}
                onPageChange={(_, p) => setPage(p)}
                rowsPerPage={pageSize}
                onRowsPerPageChange={(e) => { setPageSize(parseInt(e.target.value, 10)); setPage(0); }}
                rowsPerPageOptions={[25, 50, 100]}
                sx={{ }}
              />
              </>
            )}
          </AsecPanel>
        </Grid>

        <Grid item xs={12} md={5}>
          <EndpointDetailPanel
            ep={selected}
            drift={drift}
            navigate={navigate}
            siteId={siteId}
            tagOptions={tagOptions}
            onSavedTags={(epId, nextTags) => {
              // Patch the in-memory endpoints list so the row +
              // detail panel reflect the new labels immediately,
              // without waiting for a /catalog/endpoints refetch.
              setEndpoints((prev) =>
                Array.isArray(prev)
                  ? prev.map((e) => (e.id === epId ? { ...e, sensitivity_tags: nextTags } : e))
                  : prev
              );
              setSelected((cur) => (cur && cur.id === epId ? { ...cur, sensitivity_tags: nextTags } : cur));
            }}
          />
        </Grid>
      </Grid>
    </Stack>
  );
}

export default function AsecOverview() {
  const navigate = useNavigate();
  const [refreshKey, setRefreshKey] = React.useState(0);

  return (
    <AsecPage helpTooltip="API security at-a-glance: top-risk endpoints, coverage %, recent events, drift summary. Click an endpoint to drill into its full detail."
      title="Overview"
      helmetTitle="API Security — Overview"
      onRefresh={() => setRefreshKey((k) => k + 1)}
      headerActions={(
        <>
          <GhostButton
            startIcon={<LayerdIcon icon={ChevronLeft} size="sm" color="neutral" />}
            onClick={() => navigate(-1)}
          >
            Back
          </GhostButton>
          <PrimaryButton
            startIcon={<LayerdIcon icon={Upload} size="sm" color="inherit" />}
            onClick={() => navigate("/v2/api-security/schema")}
          >
            Import OpenAPI
          </PrimaryButton>
        </>
      )}
    >
      {(siteId) => <Body key={`${siteId}-${refreshKey}`} siteId={siteId} />}
    </AsecPage>
  );
}
